<script setup>
import { inject, onBeforeMount, ref } from 'vue';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import EnWidgetCard from 'Components/widgets/EnWidgetCard/EnWidgetCard.vue';
import { EnButtonVariants } from 'Components/types/EnButtonVariants';
import { useI18n } from 'vue-i18n';
import { getUrlParams } from 'Js/utils/urlUtils';

const { t } = useI18n();

const logoutId = ref('');
const returnUrl = ref('');
const breadcrumbInfo = ref([t('Authentication.logout.title')]);
const {updateBreadcrumbs} = inject('breadcrumb');
updateBreadcrumbs(breadcrumbInfo.value);

async function tryLogout() {
  const requestBody = {returnUrl: returnUrl.value, logoutId: logoutId.value};

  const response = await fetch(`/Account/TryLogout`, {
    method: 'POST',
    credentials: 'include', //this attaches cookies
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody),
  });
  if (response.ok) {
    if (returnUrl.value) {
      window.location.replace(returnUrl.value);
    } else {
      window.location.replace('/');
    }
  }
}

// Don't skip this page for users that look logged out on the client: EN.Auth has no client-side user state
// (useUserSettingsStore never contains the user here), so such a check redirected every user away without
// logging them out. /Account/TryLogout is a no-op for users who are already logged out.
onBeforeMount(() => {
  const params = getUrlParams(window.location);

  returnUrl.value = params['returnurl'];
  logoutId.value = params['logoutId'];
});

</script>

<template>
  <div class="main-overview restricted-width">
    <en-widget-card
        icon-class="icon-enloc_logo_min"
        :title="t('Authentication.logout.message')"
        :max-width="450"
        :padding="32"
    >
      <div class="form-group-bordered">
        <v-form class="w-100" method="post" action="/account/logout" @submit.prevent="tryLogout">
          <input type="hidden" name="logoutId" :value="logoutId"/>
          <input type="hidden" name="ReturnUrl" :value="returnUrl"/>

          <div class="login-buttons">
            <en-button
                static-id="logout-button"
                class="button-text"
                width="100%"
                :variant="EnButtonVariants.FILLED"
                no-shadow
                @click="tryLogout"
            >
              {{ t('Authentication.logout.buttonText') }}
            </en-button>

            <div class="d-flex align-center justify-center w-100">
              <v-divider class="flex-grow-1"></v-divider>
              <span class="or-text px-3">{{ t('common.or') }}</span>
              <v-divider class="flex-grow-1"></v-divider>
            </div>

            <a :href="returnUrl || '/'" class="w-100">
              <en-button
                  static-id="back-button"
                  class="button-text"
                  width="100%"
                  no-shadow
              >
                {{ t('Authentication.logout.back') }}
              </en-button>
            </a>
          </div>
        </v-form>
      </div>
    </en-widget-card>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";

.main-overview {
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.form-group-bordered {
  margin-top: 15px;
  width: 100%;
}

.login-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.login-buttons .en-base-action-button {
  width: 100%;
}

.button-text {
  border: 1px solid variables.$login-page-btn-text;
  text-transform: none;
}
</style>